import { MapPin, Phone, MessageCircle, Car, Bus } from "lucide-react";
import { useMemo } from "react";
import { Seo, faqJsonLd } from "@/components/Seo";
import { PageHead, Section, CtaBand, FaqList } from "@/components/Shared";
import { UI } from "@/i18n/ui";
import { SITE, waLink, PLACEHOLDER, ORG_JSONLD, HOURS } from "@/data/site";
import { FAQ } from "@/data/faq";

export function FaqPage({ lang }) {
  const t = UI[lang];
  const items = useMemo(() => FAQ.map((f) => [f.q[lang], f.a[lang]]), [lang]);
  const jsonLd = useMemo(() => [faqJsonLd(items)], [items]);
  const meta = {
    id: { title: "FAQ — Pertanyaan Umum tentang Layanan Kami | Bali Empower", desc: "Jawaban atas pertanyaan yang paling sering diajukan tentang konsultasi, kerahasiaan, biaya, lokasi, dan cara membuat janji di Bali Empower, Denpasar." },
    en: { title: "FAQ — Common Questions About Our Services | Bali Empower", desc: "Answers to the most frequently asked questions about consultations, confidentiality, fees, location and booking at Bali Empower, Denpasar." },
  }[lang];
  return (
    <div data-testid="faq-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={jsonLd} />
      <PageHead eyebrow="FAQ" title={t.sections.faq} />
      <Section className="pt-0">
        <div className="max-w-3xl">
          <FaqList items={items} testPrefix="faqpage" />
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

const LOCATION_JSONLD = [ORG_JSONLD];

const InfoRow = ({ Icon, label, value }) => (
  <div className="flex gap-4 border-b border-[#E5E1D8] pb-5">
    <Icon size={20} className="shrink-0 text-[#4A6B53] mt-0.5" strokeWidth={1.6} />
    <div>
      <h2 className="font-semibold font-sans text-[#1F2937] text-[15px]">{label}</h2>
      <p className="mt-1 text-sm text-[#4B5563] leading-relaxed">{value}</p>
    </div>
  </div>
);

export function LocationPage({ lang }) {
  const t = UI[lang];
  const meta = {
    id: { title: "Lokasi Klinik Psikologi di Renon, Denpasar | Bali Empower", desc: "Bali Empower berlokasi di Apotik Indobat, Jl. Tukad Yeh Aya No. 100, Renon, Denpasar Selatan, Bali 80234. Lihat peta, petunjuk arah, dan cara membuat janji." },
    en: { title: "Psychology Clinic Location in Renon, Denpasar | Bali Empower", desc: "Bali Empower is located at Apotik Indobat, Jl. Tukad Yeh Aya No. 100, Renon, Denpasar Selatan, Bali 80234. See the map, directions and how to book." },
  }[lang];
  const info = {
    id: [
      [MapPin, "Alamat", `${SITE.address.street}, ${SITE.address.area}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}, ${SITE.address.country}`],
      [Phone, "Telepon", SITE.phone],
      [MapPin, "Landmark terdekat", "Kawasan Renon — pusat perkantoran dan pemerintahan Denpasar Selatan. Klinik berada di lokasi Apotik Indobat di Jl. Tukad Yeh Aya."],
      [Car, "Parkir", PLACEHOLDER.id],
      [Bus, "Transportasi umum", PLACEHOLDER.id],
      [MessageCircle, "Jam praktik", `${HOURS.id}. Jadwal sesi bersifat tentatif — konfirmasi melalui WhatsApp.`],
    ],
    en: [
      [MapPin, "Address", `${SITE.address.street}, ${SITE.address.area}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}, ${SITE.address.country}`],
      [Phone, "Phone", SITE.phone],
      [MapPin, "Nearby landmark", "The Renon area — Denpasar Selatan's government and office district. The clinic is at the Apotik Indobat location on Jl. Tukad Yeh Aya."],
      [Car, "Parking", PLACEHOLDER.en],
      [Bus, "Public transport", PLACEHOLDER.en],
      [MessageCircle, "Practice hours", `${HOURS.en}. Session schedules are tentative — confirm via WhatsApp.`],
    ],
  }[lang];
  return (
    <div data-testid="location-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={LOCATION_JSONLD} />
      <PageHead eyebrow={t.nav.location} title={t.sections.location}
        sub={lang === "id"
          ? "Bali Empower adalah klinik psikologi dan kesehatan mental yang berlokasi di Renon, Denpasar Selatan, Bali — melayani Denpasar dan wilayah Bali yang lebih luas termasuk Sanur, Badung, Kuta, Seminyak, Canggu, Ubud, dan sekitarnya."
          : "Bali Empower is a psychology and mental health clinic located in Renon, Denpasar Selatan, Bali — serving Denpasar and the wider Bali area including Sanur, Badung, Kuta, Seminyak, Canggu, Ubud and beyond."} />
      <Section className="pt-0">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            {info.map(([Icon, label, value]) => (
              <InfoRow key={label} Icon={Icon} label={label} value={value} />
            ))}
            <div className="flex flex-wrap gap-4 pt-2">
              <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="location-maps-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white text-sm font-medium px-6 py-3 hover:bg-[#3A5542] transition-colors">
                <MapPin size={15} /> {t.misc.directions}
              </a>
              <a href={waLink(lang === "id" ? "Halo Bali Empower, saya ingin bertanya tentang lokasi dan jadwal." : "Hello Bali Empower, I would like to ask about location and schedule.")}
                target="_blank" rel="noopener noreferrer" data-testid="location-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#4A6B53] text-[#4A6B53] text-sm font-medium px-6 py-3 hover:bg-[#4A6B53]/5 transition-colors">
                <MessageCircle size={15} /> {t.cta.whatsapp}
              </a>
            </div>
          </div>
          <iframe title="Bali Empower Google Maps" src={SITE.mapsEmbed} loading="lazy"
            className="w-full min-h-[420px] rounded-2xl border border-[#E5E1D8]" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
